import { call, put, takeEvery, select } from "redux-saga/effects"

// CONSTANTS
import {
  GET_USER_INFORMATIONS,
  LOGIN_USER,
  SEND_EMAIL_RESET_PASSWORD,
  UPDATE_ONBOARDING,
} from "./constants"

// ACTIONS
import {
  userLoginSuccessAction,
  userGetInformationsAction,
  setInfosMultiBusinessAction,
  mfaRequiredAction,
} from "./actions"

import { setSuccessMessage, setErrorMessage, removeErrorMessage } from "Containers/Messages/actions"
import KeycloakService from "Services/KeycloakService"

// SELECTORS
import { selectCurrentToken } from "Containers/Login/selectors"

// UTILS
import { googleEvent } from "Utils/GA"
import {
  responseTypeIsError,
  responseTypeIsSuccess,
  manageErrorGenerator,
  manageSuccessGenerator,
} from "Containers/App/saga"

import { postCors, getCors, patchCors } from "Utils/rulesCors"
import { config } from "Utils/config"
import request from "Utils/request"

function* getLogin({ payload }) {
  const { API_URL, APOLLO_SERVER_URL } = config
  // const requestURL = API_URL + "/user/login"
  const requestURL = APOLLO_SERVER_URL + "/login/connect"

  try {
    const response = yield call(request, requestURL, postCors(payload))
    if (!response?.success) {
      const messageError = { type: "error", code: "wrongCredential", message: "wrongCredential" }
      yield call(manageErrorGenerator, { response: messageError, showMessageToUser: true })
      console.error("[route] => /login/connect => Erreur lors de la connexion")
    } else if (response?.mfaRequired) {
      // MFA challenge required — store challenge token and let UI show MfaChallenge component
      yield put(
        mfaRequiredAction({
          challengeToken: response.mfaChallengeToken,
          mfaMethod: response.mfaMethod || null,
          mfaEnrollmentRequired: !!response.mfaEnrollmentRequired,
          allowedMethods: response.allowedMethods || [],
          email: payload?.email,
        })
      )
    } else {
      if (response?.success) {
        /**
         * /!\ Attention logique post-login à répercuté sur le useEffect du main App.js dans la
         * fonction"setKeycloakLoginResponseToStore"
         */
        const infosMultiBusiness = response?.available_accounts || []
        const isMultiBusiness = infosMultiBusiness?.length > 1
        if (isMultiBusiness) {
          yield put(setInfosMultiBusinessAction(infosMultiBusiness))
        } else {
          // Login email : purge un éventuel marqueur SSO périmé d'une session
          // précédente sur ce navigateur (cf. KeycloakService.clearSsoSession).
          KeycloakService.clearSsoSession()
          yield put(userLoginSuccessAction(infosMultiBusiness[0]))
          // Stocker l'ID utilisateur dans localStorage pour détecter les changements entre onglets
          if (infosMultiBusiness[0]?.USER_ID) {
            window.localStorage.setItem(
              "app_current_user_id",
              String(infosMultiBusiness[0].USER_ID)
            )
          }
          // Persister le JWT pour Apollo's authLink (back retourne en body, pas en cookie)
          if (infosMultiBusiness[0]?.token) {
            window.localStorage.setItem("accessToken", infosMultiBusiness[0].token)
          }
        }

        window.localStorage.removeItem("businessLogo")

        googleEvent("Connexion", `${response?.infos?.FIRSTNAME} ${response?.infos?.LASTNAME}`)
      }
    }
  } catch (err) {
    console.error("[route] => /login/connect => ERROR => ", err)
    yield put(setErrorMessage(err))
  }
}

function* getUserInformations() {
  const token = yield select(selectCurrentToken)
  const specificHeaders = { token: token }

  const { API_URL } = config
  const requestURL = `${API_URL}/v2/user`

  try {
    const response = yield call(request, requestURL, getCors(specificHeaders))
    if (responseTypeIsError(response)) {
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    } else {
      if (responseTypeIsSuccess(response)) {
        yield call(manageSuccessGenerator, { response, showMessageToUser: false })
        yield put(userLoginSuccessAction(response))
        // Stocker l'ID utilisateur dans localStorage pour détecter les changements entre onglets
        if (response?.USER_ID) {
          window.localStorage.setItem("app_current_user_id", String(response.USER_ID))
        }
      }
    }
  } catch (err) {
    console.error("[route] => /user/:token => ERROR => ", err)
    yield put(setErrorMessage(err))
  }
}

function* sendEmailResetPassword({ payload }) {
  const { APOLLO_SERVER_URL } = config
  const requestURL = APOLLO_SERVER_URL + "/login/resetPassword"

  try {
    const response = yield call(request, requestURL, postCors({ email: payload }))
    if (responseTypeIsError(response)) {
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    } else {
      if (responseTypeIsSuccess(response)) {
        yield call(manageSuccessGenerator, {
          response: { ...response, code: "EMAIL_RESET_PASSWORD_BY_ADMIN_SUCCESS" },
          showMessageToUser: true,
        })
      }
    }
  } catch (err) {
    yield put(setErrorMessage(err))
  }
}

function* updateUserOnBoarding({ payload }) {
  const userId = payload?.userId
  const action = payload?.action
  const token = yield select(selectCurrentToken)
  const specificHeaders = { token: token }

  const { API_URL } = config
  const endPath = `updateUser/${userId}`
  const requestURL = `${API_URL}/${endPath}`

  try {
    const response = yield call(request, requestURL, patchCors({ action }, specificHeaders))
    if (responseTypeIsError(response))
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    else {
      if (responseTypeIsSuccess(response))
        yield call(manageSuccessGenerator, { response, showMessageToUser: false })
      yield put(userGetInformationsAction())
    }
  } catch (err) {
    console.error(`[route] => /${endPath} => ERROR => `, err)
    yield put(setErrorMessage(response))
  }
}

function* loginSaga() {
  yield takeEvery(LOGIN_USER, getLogin)
  yield takeEvery(GET_USER_INFORMATIONS, getUserInformations)
  yield takeEvery(SEND_EMAIL_RESET_PASSWORD, sendEmailResetPassword)
  yield takeEvery(UPDATE_ONBOARDING, updateUserOnBoarding)
}

export default loginSaga
